import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import Breadcrumb from "../../layout/breadcrumb";
import { CrudParam } from "../../configs/route";
import { useApollo } from "../../services/apollo.service";
import { convert_to_date, escapedNewLineToLineBreakTag, SITE_TITLE } from "../../configs/constants";
import { useAuth } from "../../hooks/auth";
import { toast } from "react-toastify";
import { gql } from "@apollo/client";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import pt_BR from "date-fns/locale/pt-BR";
import { useSelector } from "react-redux";
import { ICombineReducers } from "../../redux";
import Loading from "../../components/loading";

const Manage: React.FC = () => {
  const isMountedRef = useRef(null);
  const { apollo, error } = useApollo();
  const { id } = useParams<CrudParam>();
  const [level, setLevel] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [createdAt, setCreatedAt] = useState<Date>(null);
  const [preview] = useState<boolean>(() => { if (id) return true; else return false; });
  const locale = useSelector((state: ICombineReducers) => state.State.locale);
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();
  const intl = useIntl();

  const { hasScope } = useAuth();
  const [canView] = useState<boolean>(() => {
    return hasScope("Logs:Log");
  });
  const [canCreate] = useState<boolean>(() => {
    return hasScope("Logs:Create");
  });
  const [canUpdate] = useState<boolean>(() => {
    return hasScope("Logs:Update");
  });

  const handleData = useCallback(async () => {
    if (id) {
      if (preview && !canView) {
        toast.error(intl.formatMessage({ id: "flash.error.accessDenied" }));
        history.push("/logs");
        return;
      } else if (!preview && !canUpdate) {
        toast.error(intl.formatMessage({ id: "flash.error.accessDenied" }));
        history.push("/logs");
        return;
      }
      setLoading(true);
      await apollo.query({
        query: gql`
            query log($id: String!) {
              log(id: $id) {
                id
                createdAt
                updatedAt
                level
                message
              }
            }
          `,
        variables: {
          id
        },
      })
        .then((res) => {
          if (isMountedRef.current) {
            setLevel(res.data.log.level || "");
            setCreatedAt(res.data.log.createdAt ? convert_to_date(res.data.log.createdAt) : null);
            setMessage(res.data.log.message || "");
          }
        })
        .catch((err) => error(err));
      setLoading(false);
    } else {
      if (!preview && !canCreate) {
        toast.error(intl.formatMessage({ id: "flash.error.accessDenied" }));
        history.push("/logs");
        return;
      }
    }
  }, [intl, id, preview, apollo, error, history, canView, canCreate, canUpdate]);

  useEffect(() => {
    isMountedRef.current = true;
    document.title = `${SITE_TITLE
      } :: ${intl.formatMessage({
        id: `pages.logs.${preview ? "preview" : id ? "update" : "create"}`,
      })}`;
    if (locale === "pt") {
      registerLocale("pt-BR", pt_BR);
      setDefaultLocale("pt-BR");
    }
    handleData();
    return () => { isMountedRef.current = false; }
  }, [intl, id, preview, handleData, locale]);

  return (
    <Fragment>
      <Breadcrumb
        parent="pages.logs.logs"
        title={`pages.logs.${preview ? "preview" : id ? "update" : "create"}`}
      />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <h5>
                  <FormattedMessage
                    id={`pages.logs.${preview ? "preview" : id ? "update" : "create"
                      }`}
                  />
                </h5>
              </CardHeader>
              <CardBody className="tabbed-card">
                <div className="form-content">
                  <Form onSubmit={(e) => e.preventDefault()}>
                    <Row className="m-b-30">
                      <Col>
                        {loading ? <Loading /> : (
                          <React.Fragment>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label className="col-form-label">
                                    <FormattedMessage id="inputs.level" />
                                  </Label>
                                  <Input
                                    className="form-control"
                                    type="text"
                                    value={level.toUpperCase()}
                                    disabled={preview}
                                    required
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label className="col-form-label">
                                    <FormattedMessage id="inputs.createdAt" />
                                  </Label>
                                  <DatePicker
                                    className="form-control"
                                    showPopperArrow={false}
                                    disabled={preview}
                                    selected={createdAt}
                                    onChange={(date: Date) => setCreatedAt(date)}
                                    dateFormat="dd/MM/yyyy HH:mm"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={30}
                                    autoComplete="off"
                                    timeInputLabel="Hora"
                                    required
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label className="col-form-label">
                                    <FormattedMessage id="inputs.message" />
                                  </Label>
                                  <div style={{ whiteSpace: "pre-wrap" }}>
                                    <code style={{ display: "block", borderRadius: "10px", border: "1px solid #e6e6e6", padding: "10px" }}>{escapedNewLineToLineBreakTag(message)}</code>
                                  </div>
                                </FormGroup>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Button
                          type="button"
                          color="danger"
                          onClick={() => history.push("/logs")}
                        >
                          <i className="fa fa-arrow-left"></i>
                          <FormattedMessage id="buttons.cancel" />
                        </Button>
                      </Col>
                      <Col className="text-right">

                      </Col>
                    </Row>
                  </Form>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Manage;
